<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref, toRaw } from 'vue';

import KRadioField from '@/components/fields/KRadioField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

type ArrivedStopType = 'arrived';
type DepartedStopType = 'departed';
type CompletedStopType = 'completed';
type FutileStopType = 'futile';
type CancelStopType = 'cancel';

export interface InitialData {
  stopType?:
    | ArrivedStopType
    | DepartedStopType
    | CompletedStopType
    | FutileStopType
    | CancelStopType
    | null;
}

// TODO make types depend on other props (stopNum)
export interface ValidData {
  stopType:
    | ArrivedStopType
    | DepartedStopType
    | CompletedStopType
    | FutileStopType
    | CancelStopType;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
  stopNum?: number;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
  stopNum: undefined,
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const arrivedStopType: ArrivedStopType = 'arrived';
const departedStopType: DepartedStopType = 'departed';
const completedStopType: CompletedStopType = 'completed';
const futileStopType: FutileStopType = 'futile';
const cancelStopType: CancelStopType = 'cancel';

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, function (event) {
  emit('submit', event, toRaw(formData) as ValidData);
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.chooseOptionStopDialogForm"
    data-testid="chooseOptionStopDialogForm"
    @submit.prevent="handleSubmit"
  >
    <v-radio-group
      v-model="formData.stopType"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.radioGroup"
    >
      <template v-if="props.stopNum">
        <KRadioField
          :label="`The driver arrived at Stop ${props.stopNum}`"
          :value="arrivedStopType"
        />
        <KRadioField
          :label="`The driver departed from Stop ${props.stopNum}`"
          :value="departedStopType"
        />
      </template>
      <template v-else>
        <KRadioField
          label="The driver completed all stops"
          :value="completedStopType"
        />
        <KRadioField label="Close the job as futile" :value="futileStopType" />
        <KRadioField label="Cancel the job" :value="cancelStopType" />
      </template>
    </v-radio-group>
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.chooseOptionStopDialogForm {
}
  .field {
  }
  .radioGroup {
    margin-bottom: 0.5rem;
  }
</style>
